import type { ComponentType, ReactNode } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { OverflowText } from '../layout/Typography';
import { useActiveOrgSlug } from '@reflex/ui/client/context';
import { withOrgSlug } from '@reflex/shared/routes';
import { cn } from '../../lib/utils';

/** HoverCardContent chrome for popovers that wrap a resource preview. */
export const RESOURCE_PREVIEW_HOVER_CONTENT_CLASS =
  'w-80 rounded-2xl p-3 shadow-[0_12px_12px_rgba(0,0,0,0.25)]';

/**
 * Figma's session hover uses a pill for a one-or-two-word state ("Idle").
 * Longer captions stay plain muted text so the capsule does not wrap a sentence.
 */
export function isCompactPreviewSubtitle(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  return trimmed.length <= 24 && trimmed.split(/\s+/).length <= 3;
}

export interface ResourcePreviewCardProps {
  /** Human-readable kind label, e.g. "Persona", "Blueprint", "MCP Server". */
  kindLabel: string;
  /** Icon component for the kind (resolved from the registry by callers). */
  icon: ComponentType<{ className?: string }>;
  /**
   * Optional visual rendered in the header's leading slot in place of the
   * default icon (e.g. a user avatar). When omitted, `icon` is rendered
   * inside the circular glyph.
   */
  media?: ReactNode;
  /** Display name of the entity. */
  name: string;
  /** Optional status / state. Short labels render as a pill; longer copy stays plain. */
  subtitle?: string;
  /** Optional description / body content. */
  description?: ReactNode;
  /** Meta chips row rendered above the description (e.g. model, repo). */
  meta?: ReactNode;
  /**
   * Owner indicator rendered on the title row. Typically an owner chip
   * that resolves flat ownership columns into the standard
   * user/team/organization chip + preview popover.
   */
  owner?: ReactNode;
  /**
   * Optional href template with `:id` placeholder for the "Open in {label}"
   * footer link. When provided, the link button is rendered.
   */
  detailHref?: string;
  /** Resource id used to substitute `:id` in `detailHref`. */
  id: string;
  /** Override the footer link label. Defaults to `Open in {kindLabel}s`. */
  openLinkLabel?: string;
  /** Class name forwarded to the outer card. */
  className?: string;
}

/**
 * Shared visual shell rendered inside every resource preview popover/hover-card.
 * Owns the chrome (glyph, name, kind · id, "Open in {label}" link, layout)
 * so plugin-provided previews only have to render their kind-specific body.
 */
export function ResourcePreviewCard({
  kindLabel,
  icon: Icon,
  media,
  name,
  subtitle,
  description,
  meta,
  owner,
  detailHref,
  id,
  openLinkLabel,
  className,
}: ResourcePreviewCardProps) {
  const slug = useActiveOrgSlug();
  const resolvedHref = detailHref ? withOrgSlug(slug, detailHref.replace(':id', id)) : null;
  const linkLabel = openLinkLabel ?? `Open in ${kindLabel}s`;
  const kindIdTitle = `${kindLabel} · ${id}`;

  return (
    <div className={cn('flex flex-col gap-3', className)} data-testid="resource-preview-card">
      <div className="flex items-start gap-2.5">
        {media ? (
          <div className="shrink-0" data-testid="resource-preview-media">
            {media}
          </div>
        ) : (
          <div className="flex size-8 shrink-0 items-center justify-center rounded-[22px] bg-sidebar-accent">
            <Icon className="size-4 text-muted-foreground" />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex w-full items-center gap-2">
            <OverflowText
              as="p"
              className="min-w-0 flex-1 text-[13px] font-semibold leading-none"
              title={name}
              data-testid="resource-preview-name"
            >
              {name}
            </OverflowText>
            {owner ? (
              <div className="shrink-0" data-testid="resource-preview-owner">
                {owner}
              </div>
            ) : null}
          </div>
          <div
            className="flex w-full min-w-0 items-center gap-1 whitespace-nowrap"
            title={kindIdTitle}
          >
            <span
              className="shrink-0 text-xs text-muted-foreground"
              data-testid="resource-preview-kind"
            >
              {kindLabel} ·
            </span>
            <OverflowText
              as="span"
              className="min-w-0 flex-1 font-mono text-[11px] text-muted-foreground select-all"
              title={id}
              data-testid="resource-preview-id"
            >
              {id}
            </OverflowText>
          </div>
          {subtitle ? (
            isCompactPreviewSubtitle(subtitle) ? (
              <span
                className="inline-flex w-fit max-w-full self-start rounded-2xl border px-2 py-0.5"
                title={subtitle}
                data-testid="resource-preview-subtitle"
              >
                <OverflowText as="span" className="text-xs leading-[17px] text-muted-foreground">
                  {subtitle}
                </OverflowText>
              </span>
            ) : (
              <OverflowText
                as="span"
                className="text-xs text-muted-foreground"
                title={subtitle}
                data-testid="resource-preview-subtitle"
              >
                {subtitle}
              </OverflowText>
            )
          ) : null}
        </div>
      </div>

      {meta ? <div className="flex flex-wrap gap-2">{meta}</div> : null}

      {description ? (
        <div className="space-y-1.5 text-[12px] leading-normal text-muted-foreground">
          {description}
        </div>
      ) : null}

      {resolvedHref ? (
        <Button
          asChild
          size="sm"
          variant="secondary"
          className="h-8 w-full gap-2 rounded-[10px] border border-border text-xs font-medium"
        >
          <Link to={resolvedHref} aria-label={`${linkLabel} for ${name}`}>
            <ArrowUpRight className="size-3.5" />
            {linkLabel}
          </Link>
        </Button>
      ) : null}
    </div>
  );
}
